import { useLocation } from 'react-router-dom';

import { useAuth } from '@/nucleo/auth/useAuth';
import type { StatusTone } from '@/nucleo/statusBadgeTokens';

import type { OrigemSolicitacaoSme, StatusSolicitacaoSme, TipoSolicitacaoSme } from './tipos';

export const ROTULO_TIPO: Record<TipoSolicitacaoSme, string> = {
  NOVA_ROTA: 'Nova rota',
  ALTERACAO_ITINERARIO: 'Alteração de itinerário',
  INCLUSAO_ALUNO: 'Inclusão de aluno',
  EXCLUSAO_ALUNO: 'Exclusão de aluno',
  TROCA_VEICULO: 'Troca de veículo',
  TROCA_CONDUTOR: 'Troca de condutor',
  OUTROS: 'Outros',
  ESCALONAMENTO_APONTAMENTO: 'Escalonamento de apontamento',
  QUESTIONAMENTO_OS: 'Questionamento sobre OS',
  REVISAO_BANCADA_VEICULO: 'Revisão de bancada de veículo',
};

/** Tipos que só fazem sentido sobre uma unidade — o formulário exige escola. */
export const TIPOS_QUE_EXIGEM_ESCOLA: readonly TipoSolicitacaoSme[] = [
  'INCLUSAO_ALUNO',
  'EXCLUSAO_ALUNO',
  'ALTERACAO_ITINERARIO',
  'NOVA_ROTA',
];

export const ROTULO_ORIGEM: Record<OrigemSolicitacaoSme, string> = {
  SME: 'SME → DTP',
  DTP: 'DTP → SME',
};

/** Rótulo e tom de cada estado — a política de status do protótipo (`statusPolicy`). */
export const APRESENTACAO_DO_STATUS: Record<
  StatusSolicitacaoSme,
  { rotulo: string; tom: StatusTone; descricao: string }
> = {
  ABERTA: {
    rotulo: 'Em triagem (SME)',
    tom: 'warning',
    descricao: 'Aguardando triagem inicial da DTP.',
  },
  EM_ANALISE: {
    rotulo: 'Em análise (SME)',
    tom: 'warning',
    descricao: 'DTP analisando a demanda.',
  },
  ENCAMINHADA_DRE: {
    rotulo: 'Encaminhada à DRE',
    tom: 'info',
    descricao: 'DTP encaminhou para a DRE responder.',
  },
  RESPONDIDA_DRE: {
    rotulo: 'Respondida pela DRE',
    tom: 'info',
    descricao: 'DRE respondeu; DTP precisa concluir a tratativa.',
  },
  ATENDIDA: {
    rotulo: 'Atendida',
    tom: 'success',
    descricao: 'Demanda finalizada com atendimento.',
  },
  RECUSADA: {
    rotulo: 'Recusada',
    tom: 'destructive',
    descricao: 'DTP recusou a demanda (motivo registrado).',
  },
  CANCELADA: {
    rotulo: 'Cancelada',
    tom: 'neutral',
    descricao: 'SME cancelou a demanda antes do desfecho.',
  },
};

export const STATUS_ENCERRADOS: readonly StatusSolicitacaoSme[] = [
  'ATENDIDA',
  'RECUSADA',
  'CANCELADA',
];

export type EstadoSla = 'vencido' | 'vencendo' | 'ok' | 'sem_prazo';

/** Estado do prazo desejado na lista — até 48 h conta como "vencendo". */
export function estadoSla(prazo: string | null, status: StatusSolicitacaoSme): EstadoSla {
  if (STATUS_ENCERRADOS.includes(status)) return 'ok';
  if (!prazo) return 'sem_prazo';
  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);
  const dias = Math.round((new Date(`${prazo}T00:00:00`).getTime() - hoje.getTime()) / 86_400_000);
  if (dias < 0) return 'vencido';
  if (dias <= 2) return 'vencendo';
  return 'ok';
}

export function formatarData(iso: string, comHora = false): string {
  const data = iso.length === 10 ? new Date(`${iso}T00:00:00`) : new Date(iso);
  return new Intl.DateTimeFormat(
    'pt-BR',
    comHora ? { dateStyle: 'short', timeStyle: 'short' } : { dateStyle: 'short' },
  ).format(data);
}

const BASE_SME = '/sme/demandas';
const BASE_FINANCEIRO = '/operacao/financeiro/solicitacoes-sme';

/**
 * De que casca a tela está sendo servida. A mesma tela vive no portal SME e no
 * Financeiro; lista e detalhe precisam manter o usuário na casca em que entrou
 * — mesmo conserto de `useFechamentoBasePath`.
 */
export function useBaseDasSolicitacoes(): string {
  const { pathname } = useLocation();
  return pathname.startsWith(BASE_SME) ? BASE_SME : BASE_FINANCEIRO;
}

/**
 * O que cada papel pode fazer, nos termos do protótipo (`useSmeDrePermissoes`
 * e as regras do próprio detalhe). Nada disto grava — decide só o que é
 * renderizado (CLAUDE.md, regras de interface, item 7).
 */
export function usePermissoesDeSolicitacao() {
  const { temPapel } = useAuth();
  const admin = temPapel('ADMIN', 'GESTOR_ADMIN');
  const financeiro = temPapel('FINANCEIRO', 'GESTOR');
  const sme = temPapel('OPERADOR_SME', 'GESTOR_SME');
  const dre = temPapel('OPERADOR_DRE', 'GESTOR_DRE');
  const podeAbrirPelaSme = admin || temPapel('GESTOR_SME');
  return {
    /** Abrir solicitação pela SME (`podeEditarDemandasSme`). */
    podeAbrirPelaSme,
    /** Atuar como SME no triângulo: encaminhar questionamento à DRE. */
    atuaComoSme: admin || sme,
    /** DRE que não é também SME — responde o que a SME encaminhou. */
    atuaComoDre: !admin && !sme && dre,
    /** Lado DTP — vê o recorte da DRE e da escola de quem pediu. */
    atuaComoDtp: admin || financeiro,
    /**
     * Sentido de uma solicitação nova quando a tela não o fixa: DTP para quem
     * só tem papel de Financeiro, SME para os demais — regra do protótipo.
     */
    origemPadrao: (financeiro && !sme && !dre ? 'DTP' : 'SME') as OrigemSolicitacaoSme,
    /**
     * Quem está do lado que abriu. O protótipo compara com `criado_por`; o mock
     * não tem autor real, então o lado de quem abriu faz as vezes de autor. Do
     * lado SME, o mesmo corte de `podeEditarDemandasSme` (admin ou GESTOR_SME):
     * OPERADOR_SME lê, não abre nem cancela em nome da SME.
     */
    ehDoLadoQueAbriu: (origem: OrigemSolicitacaoSme) =>
      origem === 'SME' ? podeAbrirPelaSme : admin || financeiro,
  };
}
